import React, { Suspense, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/config/i18n';
import { isLangPrefix } from '@/config/langPrefixes';

/** Per-language cloned i18n instances. Each clone has its own independent
 *  `language` field while sharing resources, so concurrent SSG renders can't
 *  race each other through the singleton. */
const i18nByLang = new Map<string, ReturnType<typeof i18n.cloneInstance>>();
function getI18nForLang(lang: string) {
  let inst = i18nByLang.get(lang);
  if (!inst) {
    inst = i18n.cloneInstance({ lng: lang });
    i18nByLang.set(lang, inst);
  }
  return inst;
}

// Lazy-load all i18n blog modules via Vite glob
const blogI18nModules = import.meta.glob<{ default: React.ComponentType }>(
  '../pages/blog/i18n/**/*.tsx'
);

// Map blog slug to i18n filename — kept empty after the 2026-05 refactor.
// All 47 posts now render the EN component for every language and read
// localized strings via useTranslation() from translation.json. The
// per-lang fallback logic below is preserved as scaffolding in case a
// future post is intentionally written as a fully-translated component
// (rare; the i18n JSON path is preferred).
const SLUG_TO_FILE: Record<string, string> = {};

interface BlogPostWrapperProps {
  slug: string;
  englishComponent: React.LazyExoticComponent<React.ComponentType>;
}

export default function BlogPostWrapper({ slug, englishComponent: EnglishComponent }: BlogPostWrapperProps) {
  const params = useParams();
  const location = useLocation();
  // Determine language from URL path directly. The /:lang/blog/:slug routes
  // are generated as literal paths (e.g. "pl/blog/dori-in-cctv") with no
  // dynamic :lang param, so params.lang is always undefined here. Reading
  // i18n.language from useLanguage was unreliable during SSG — state could
  // leak across page renders since i18n is a module-level singleton, causing
  // EN URLs to render the localized component (observed for 3 blogs).
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const langFromUrl = firstSegment && isLangPrefix(firstSegment) ? firstSegment : 'en';
  const effectiveLang = params.lang || langFromUrl;

  // Use a per-language cloned i18n instance so concurrent SSG renders don't
  // clobber each other's language via the shared singleton.
  const langI18n = getI18nForLang(effectiveLang);

  const Component = useMemo(() => {
    if (effectiveLang !== 'en') {
      const filename = SLUG_TO_FILE[slug];
      if (filename) {
        const modulePath = `../pages/blog/i18n/${effectiveLang}/${filename}.tsx`;
        const loader = blogI18nModules[modulePath];
        if (loader) {
          return React.lazy(loader);
        }
      }
    }

    return EnglishComponent;
  }, [slug, effectiveLang, EnglishComponent]);

  return (
    <I18nextProvider i18n={langI18n}>
      <Suspense fallback={<div className="min-h-screen bg-white dark:bg-graphite-900" />}>
        <Component />
      </Suspense>
    </I18nextProvider>
  );
}
